import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Modal, Row, Col, Spinner, Form as BSForm } from "react-bootstrap";
import styled from "styled-components";
import {
	CustomButton,
	CustomSelect,
	CustomInput,
	FormikFieldError,
} from "components/CustomForm";
import { MdClose } from "react-icons/md";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { getPermissions, createUser, updateUser, getUserGroups } from 'network/apis/UserGroupsApi';
import { getCorporations } from 'network/apis/CorporationApi';
import { isBlank, getErrorMessage } from "helpers/strings";

const ModalHolder = styled(Modal)`
	font-family: 'YEKAN-BAKH-MEDIUM';
`;

const FormHolder = styled(Form)`
	direction: rtl;
`;

const Header = styled.div`
	font-size: 18pt;
	width: 100%;
	text-align: center;
	margin-top: 0.5rem;
	margin-bottom: 1.5rem;
`;

const Check = styled(BSForm.Check)`
    display: inline;
`;

const CloseButton = styled.div`
	background-color: #424250;
	width: 2rem;
	height: 2rem;
	border-radius: 8px;
	display: flex;
	justify-content: center;
	align-items: center;
	color: white;
	position: absolute;
	top: 12px;
	right: 10px;
`;

const AccessLevelsBox = styled.div`
	border: 1px solid rgba(0, 0, 0, 0.33);
	border-radius: 12px;
	width: 100%;	
	position: relative;
	color: rgba(0, 0, 0, 0.3);
	margin-top: 2rem;
    padding-top: 1rem;
	height: 14rem;
	direction: ltr;

	.label {
		border: 1px solid rgba(0, 0, 0, 0.33);
		border-radius: 8px;
		position: absolute;
		background-color: white;
		top: -0.9rem;
		right: 1rem;
		padding: 0.2rem 0.7rem;
		font-size: 10pt;
		z-index: 2;
	}

	.rows {
		overflow-y: scroll;				
		height: 100%;
	}
	.level-row {		
		width: 100%;
		display: flex;
		justify-content: end;
		align-items: center;
		padding: 0.6rem 1.5rem;
	}
`;

const GroupAccessType = {
	corporation: { label: 'شرکت تعاونی', value: 0 },
	union: { label: 'استانی', value: 1 },
}

const DefaultUserType = { label: 'کاربر اتحادیه استانی', value: GroupAccessType.union };

const AddUserModal = ({
	isOpen,
	onClose,
	onCreateSuccess,
	data,
	...rest
}) => {
	const [currentData, setCurrentData] = useState();
	const [loading, setLoading] = useState(false);
	const [selectedUserType, setSelectedUserType] = useState(DefaultUserType);
	const [selectedUserGroup, setSelectedUserGroup] = useState();
	const [selectedCorporation, setSelectedCorporation] = useState();
	const [isActive, setIsActive] = useState(true);
	const [selectedPermissions, setSelectedPermissions] = useState([]);
	const [formikKey, setFormikKey] = useState(0);

	const unionId = parseInt(localStorage.getItem('union_id') ?? '0');

	const isEditMode = useCallback(
		() => currentData && currentData != null,
		[currentData]
	);

	useEffect(() => {
		setCurrentData(data);
	}, [data]);

	useEffect(() => {
		if (currentData && currentData != null) {
			if (currentData.user_type === GroupAccessType.union.value) {
				setSelectedUserType(GroupAccessType.union);
			} else if (currentData.user_type === GroupAccessType.corporation.value) {
				setSelectedUserType(GroupAccessType.corporation);
				setSelectedCorporation({
					label: currentData?.union?.name,
					value: currentData?.union?.coop_id,
				});
			}

			setIsActive(currentData.is_active);
			setSelectedPermissions(currentData.user_permissions);
		} else {
			setSelectedUserType(DefaultUserType);
			setIsActive(true);
			setSelectedPermissions([]);
			setSelectedCorporation(null);
		}

		setFormikKey((prev) => prev + 1);
	}, [currentData]);

	useEffect(() => {
		if (selectedUserGroup) {
			setSelectedPermissions(selectedUserGroup.permissions);
		}
	}, [selectedUserGroup]);

	const { data: permissions } = useQuery(
		["get-permissions"],
		() => getPermissions(`?user_type=1`),
		{
			refetchOnWindowFocus: false,
		}
	);

	const userMutation = useMutation(
		isEditMode() ? updateUser : createUser,
		{
			onSuccess: (res) => {
				if (res && res.data) {
					toast(`با موفقیت ${isEditMode() ? "ویرایش" : "ایجاد"} گردید`, {
						type: "success",
						position: "bottom-right",
					});
					onCreateSuccess?.();
					onClose();
				}
			},
			onError: (err) => {
				toast(getErrorMessage(err), {
					type: "error",
					position: "bottom-right",
				});
			},
			onSettled: () => {
				setLoading(false);
			},
		}
	);

	const { data: corporationsData } = useQuery(["get-corporations"], () => getCorporations(), {
		refetchOnWindowFocus: false,
	});
	const getCorporationOptions = () =>
		corporationsData?.data
			? corporationsData.data.map((corporation, index) => {
				return { value: corporation?.coop_id, label: corporation?.name };
			})
			: [];

	const getUserTypeOptions = () => [
		{ value: GroupAccessType.union.value, label: 'کاربر اتحادیه استانی' },
		{ value: GroupAccessType.corporation.value, label: 'کاربر شرکت تعاونی' }
	];

	const { data: groupsData } = useQuery(["province-get-user-groups-modal"], () => getUserGroups(), {
		refetchOnWindowFocus: false,
	});
	const getUserGroupsOptions = () =>
		groupsData?.data
			? groupsData.data.map((group, index) => {
				return { value: group?.id, label: group?.name, permissions: group.permissions };
			})
			: [];

	const getGroupTypeOptions = () => [GroupAccessType.admin, GroupAccessType.union];

	const onPermissionCheck = e => {
		const id = e.target.id;
		const checked = e.target.checked;

		const currentPermissions = [...selectedPermissions];
		if (checked) {
			currentPermissions.push(id);
		} else {
			const index = currentPermissions.findIndex((item) => item === id);
			currentPermissions.splice(index, 1);
		}

		setSelectedPermissions(currentPermissions);
	};

	const isChecked = permissionId => selectedPermissions
		.findIndex((item) => `${item}` === `${permissionId}`) > -1;

	const onIsActiveCheck = e => {
		const checked = e.target.checked;
		setIsActive(checked);
	};

	const isCompanyUser = () => selectedUserType?.value === GroupAccessType.corporation.value;

	return (
		<ModalHolder show={isOpen} size={'lg'} centered animation={false} onHide={onClose} {...rest}>
			<Modal.Body className="px-5">
				<CloseButton onClick={onClose}>
					<MdClose color="#fff" />
				</CloseButton>
				<Header>
					<span>{isEditMode() ? "ویرایش" : "افزودن"} کاربر</span>
				</Header>
				<Formik
					key={formikKey}
					initialValues={{
						name: currentData?.name,
						full_name: currentData?.full_name,
						phone_number: currentData?.phone_number,
						username: currentData?.username,
						national_code: currentData?.national_code,
						...(isEditMode() && { password: 'xxxxxxxx' }),
						description: currentData?.description,
					}}
					validate={(values) => {
						const errors = {};
						if (isBlank(values.full_name)) {
							errors.full_name = "ضروری";
						}
						if (isBlank(values.phone_number)) {
							errors.phone_number = "ضروری";
						}
						if (isBlank(values.username)) {
							errors.username = "ضروری";
						}
						if (isBlank(values.password)) {
							errors.password = "ضروری";
						}
						if (isBlank(values.description)) {
							errors.description = "ضروری";
						}
						if (!selectedUserType) {
							errors.user_type = "ضروری";
						} else if (!selectedCorporation && isCompanyUser()) {
							errors.union_id = "ضروری";
						}
						return errors;
					}}
					onSubmit={(values, { setSubmitting }) => {
						setLoading(true);

						const formData = {
							id: currentData?.user_id,
							user_permissions: selectedPermissions.map((p) => parseInt(p)),
							union_id: isCompanyUser() ? selectedCorporation?.value : unionId,
							is_superuser: false,
							is_active: isActive,
							groups: [],
							user_type: selectedUserType?.value,
							...values,
						};
						if (isEditMode()) delete formData.password;

						userMutation.mutate(formData);
					}}
				>
					{({ submitForm }) => (
						<FormHolder>
							<Fragment>
								<Row className="d-flex justify-content-between mb-2">
									<Col md={4}>
										<Field type="text" name="user_type">
											{({ field, meta: { touched, error } }) => (
												<CustomSelect
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="انتخاب نوع کاربر"
													radius="14"
													options={getUserTypeOptions()}
													onChange={setSelectedUserType}
													value={selectedUserType}
													menuPosition='fixed'
												/>
											)}
										</Field>
										<ErrorMessage
											name="user_type"
											component={FormikFieldError}
										/>
									</Col>
									{isCompanyUser() && (
										<Col md={4}>
											<Field type="text" name="union_id">
												{({ field, meta: { touched, error } }) => (
													<CustomSelect
														className={touched && error ? "invalid" : ""}
														error={!isBlank(error)}
														{...field}
														placeholder="انتخاب شرکت"
														radius="14"
														options={getCorporationOptions()}
														onChange={setSelectedCorporation}
														value={selectedCorporation}
														menuPosition='fixed'
													/>
												)}
											</Field>
											<ErrorMessage
												name="union_id"
												component={FormikFieldError}
											/>
										</Col>
									)}
									<Col md={isCompanyUser() ? 4 : 8}>
										<Field type="text" name="full_name">
											{({ field, meta: { touched, error } }) => (
												<CustomInput
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="نام و نام خانوادگی"
													radius="14"
												/>
											)}
										</Field>
										<ErrorMessage name="full_name" component={FormikFieldError} />
									</Col>
								</Row>
								<Row className="d-flex justify-content-between mb-2">
									<Col md={4}>
										<Field type="text" name="user_group">
											{({ field, meta: { touched, error } }) => (
												<CustomSelect
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="گروه کاربری"
													radius="14"
													options={getUserGroupsOptions()}
													onChange={setSelectedUserGroup}
													value={selectedUserGroup}
													menuPosition='fixed'
												/>
											)}
										</Field>
										<ErrorMessage
											name="user_group"
											component={FormikFieldError}
										/>
									</Col>
									<Col md={4}>
										<Field type="text" name="username" autoComplete='off'>
											{({ field, meta: { touched, error } }) => (
												<CustomInput
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="نام کاربری"
													radius="14"
												/>
											)}
										</Field>
										<ErrorMessage name="username" component={FormikFieldError} />
									</Col>
									<Col md={4}>
										<Field type="password" name="password" autoComplete='off'>
											{({ field, meta: { touched, error } }) => (
												<CustomInput
													type='password'
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="رمز ورود"
													radius="14"
													disabled={isEditMode()}
												/>
											)}
										</Field>
										<ErrorMessage name="password" component={FormikFieldError} />
									</Col>
								</Row>
								<Row className="d-flex justify-content-between mb-2">
									<Col md={4}>
										<Field type="text" name="phone_number">
											{({ field, meta: { touched, error } }) => (
												<CustomInput
													type='number'
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="شماره همراه"
													radius="14"
												/>
											)}
										</Field>
										<ErrorMessage name="phone_number" component={FormikFieldError} />
									</Col>
									<Col md={4}>
										<Field type="text" name="national_code">
											{({ field, meta: { touched, error } }) => (
												<CustomInput
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="کد ملی"
													radius="14"
												/>
											)}
										</Field>
										<ErrorMessage name="national_code" component={FormikFieldError} />
									</Col>
									<Col md={4}>
										<Field type="text" name="description">
											{({ field, meta: { touched, error } }) => (
												<CustomInput
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="توضیحات"
													radius="14"
												/>
											)}
										</Field>
										<ErrorMessage name="description" component={FormikFieldError} />
									</Col>
								</Row>
								<Row className="mt-3">
									<Col md={2} className="d-flex align-items-center justify-content-center">
										<Check
											type="checkbox"
											id='is_active'
											className='ms-3'
											onChange={onIsActiveCheck}
											checked={isActive}
										/>
										<label htmlFor='is_active'>
											{isActive ? 'فعال' : 'غیر فعال'}
										</label>
									</Col>
								</Row>
								<AccessLevelsBox>
									<div className="label">سطح دسترسی ها</div>
									<div className="rows">
										{permissions?.data?.map((permission, index) => (
											<div className="level-row" key={permission.id}>
												<label className='me-4' htmlFor={permission.id}>
													{permission.name}
												</label>
												<Check
													type="checkbox"
													id={permission.id}
													onChange={onPermissionCheck}
													checked={isChecked(permission.id)}
												/>
											</div>
										))}
									</div>
								</AccessLevelsBox>
								<div className="d-flex justify-content-center mt-4 mb-3 w-100">
									<CustomButton
										fontSize={16}
										onClick={submitForm}
										disabled={loading}
										className="w-25 d-flex align-items-center justify-content-center"
									>
										{loading ? (
											<Spinner animation="border" role="status" size="sm" />
										) : (
											<span>ثبت</span>
										)}
									</CustomButton>
									<CustomButton
										className="me-3 w-25"
										color="#7300FF"
										fontSize={16}
										onClick={onClose}
									>
										انصراف
									</CustomButton>
								</div>
							</Fragment>
						</FormHolder>
					)}
				</Formik>
			</Modal.Body>
		</ModalHolder>
	);
};

export default AddUserModal;
